import * as Sentry from '@sentry/nextjs';
import { GENERIC_ERROR_MESSAGE } from '@constants';
import { ENTITIES_PERMISIONS_NAMES } from 'components/common/PermissionsGate/constants';

/**
// Old
  await request.get('/v1/reports', { responseType: 'arraybuffer' });

// New
  await apiClient.get('/v1/reports', { responseType: 'arraybuffer' });

 * Important: Remove the body parameter from the request POST, PUT, PATCH function and keep payload as is.
*/

class ApiClient {
  constructor(baseURL = '') {
    this.baseURL = baseURL;
  }

  async request(url, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      params = null,
      responseType = 'json',
      skipAuth = false,
      skipContentType = false,
    } = options;

    let fullUrl = this.baseURL + url;

    if (params) {
      const queryString = new URLSearchParams(params).toString();
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString;
    }

    const requestHeaders = ApiClient.buildHeaders({
      headers,
      body,
      skipAuth,
      skipContentType,
    });

    const config = {
      method,
      headers: requestHeaders,
    };

    if (body !== null && method !== 'GET' && method !== 'HEAD') {
      config.body = ApiClient.prepareBody(body, requestHeaders);
    }

    try {
      const response = await fetch(fullUrl, config);
      return await ApiClient.handleResponse(response, responseType);
    } catch (error) {
      return ApiClient.handleError(error);
    }
  }

  static buildHeaders({ headers, body, skipAuth, skipContentType }) {
    const requestHeaders = { ...headers };

    if (!skipAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        requestHeaders.Authorization = token;
      }
    }

    if (!skipContentType && !requestHeaders['Content-Type']) {
      if (body && !(body instanceof FormData)) {
        requestHeaders['Content-Type'] = 'application/json';
      }
    }

    return requestHeaders;
  }

  static prepareBody(body, headers) {
    if (
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof File
    ) {
      return body;
    }

    if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
      return body;
    }

    if (typeof body === 'string') {
      return body;
    }

    if (headers['Content-Type']?.includes('application/json')) {
      return JSON.stringify(body);
    }

    return JSON.stringify(body);
  }

  static async handleResponse(response, responseType) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.replace(`${window.location.origin}/login`);
      }
      return { error: true, code: 401, message: 'Unauthorized' };
    }

    if (!response.ok) {
      return ApiClient.generateErrorResponse(response);
    }

    try {
      switch (responseType) {
        case 'arraybuffer':
          return await response.arrayBuffer();
        case 'blob':
          return await response.blob();
        case 'text':
          return await response.text();
        case 'json':
        default: {
          const text = await response.text();
          if (!text) return null;
          const data = JSON.parse(text);
          if (data?.error) {
            return ApiClient.generateErrorResponse(response, data);
          }
          return data;
        }
      }
    } catch (err) {
      return { error: true, message: 'Failed to parse response' };
    }
  }

  static async generateErrorResponse(response, data = null) {
    try {
      if (!data) {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      }

      let message = data?.error || data?.message || GENERIC_ERROR_MESSAGE;

      // Handle 403 permission errors
      if (response.status === 403) {
        const permissionName = ENTITIES_PERMISIONS_NAMES[data?.data] || '';
        message = `Your role is missing a permission [${permissionName}]. Contact an administrator.`;
      }

      Sentry.captureException(new Error(message));

      return {
        error: true,
        code: response.status,
        message,
      };
    } catch (err) {
      const message = response?.statusText || GENERIC_ERROR_MESSAGE;
      Sentry.captureException(new Error(message));

      return {
        error: true,
        code: response.status,
        message,
      };
    }
  }

  static handleError(error) {
    Sentry.captureException(error);
    return {
      error: true,
      message: error.message || GENERIC_ERROR_MESSAGE,
    };
  }

  get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  post(url, body, options = {}) {
    return this.request(url, { ...options, method: 'POST', body });
  }

  put(url, body, options = {}) {
    return this.request(url, { ...options, method: 'PUT', body });
  }

  patch(url, body, options = {}) {
    return this.request(url, { ...options, method: 'PATCH', body });
  }

  delete(url, options = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
export default apiClient;
